import React from 'react';
import { Helmet } from 'react-helmet-async';

interface FAQItem {
  question: string;
  answer: string;
}

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  canonicalPath?: string;
  imageUrl?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  schema?: object;
  faqItems?: Array<{question: string, answer: string}>;
  keywords?: string;
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
}

export function SEO({
  title = "btoolme - Find the Perfect Tools for Your Business in Minutes | Personalized Software Blueprint",
  description = "Skip the hours of research. btoolme builds a personalized software blueprint for your exact business needs in minutes. Get tailored recommendations for startups, freelancers, and small businesses.",
  path = "",
  canonicalPath,
  imageUrl = "https://btoolme.com/social-preview.png",
  type = "website",
  publishedTime,
  modifiedTime,
  author = "btoolme",
  schema,
  faqItems,
  keywords,
  noindex = false,
  breadcrumbs,
}: SEOProps) {
  const url = `https://btoolme.com${path}`;
  const canonical = canonicalPath ? `https://btoolme.com${canonicalPath}` : url;
  
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "btoolme",
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "Software Recommendation Platform",
    "description": description,
    "url": url,
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "29",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Personalized software recommendations",
      "Business profile-based tool matching",
      "Avoid List of tools to skip",
      "Growth path guidance",
      "100+ business tools database"
    ]
  };

  // Create FAQ schema if FAQ items are provided
  const faqSchema = faqItems ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // Organization schema for brand recognition
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "btoolme",
    "url": "https://btoolme.com",
    "logo": "https://btoolme.com/favicon.svg",
    "description": "Business tool recommendation platform helping entrepreneurs find the perfect software stack for their business needs.",
    "foundingDate": "2024",
    "sameAs": [
      "https://twitter.com/btoolme"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "email": "btoolmepro@gmail.com"
    }
  };

  const slugToLabel: Record<string, string> = {
    'tools': 'All Tools',
    'categories': 'Categories',
    'directory': 'Tool Directory',
    'questionnaire': 'Get Recommendations',
    'pricing': 'Pricing',
    'what-software-do-i-need-for-my-business': 'What Software Do I Need?',
    'tools-needed-to-start-a-business': 'Tools to Start a Business',
    'what-software-should-a-small-business-use': 'Small Business Software Guide',
    'for-trades': 'Software for Trades',
  };

  function humanizeSlug(slug: string): string {
    if (slugToLabel[slug]) return slugToLabel[slug];
    return slug
      .replace(/-and-/g, ' & ')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  const breadcrumbItems: Array<{name: string; item: string}> = [
    { name: 'Home', item: 'https://btoolme.com' }
  ];

  if (breadcrumbs && breadcrumbs.length > 0) {
    breadcrumbs.forEach(crumb => {
      breadcrumbItems.push({ name: crumb.name, item: `https://btoolme.com${crumb.path}` });
    });
  } else if (path) {
    const pathParts = path.split('/').filter(part => part);
    let currentPath = "";
    pathParts.forEach(part => {
      currentPath += "/" + part;
      breadcrumbItems.push({ name: humanizeSlug(part), item: `https://btoolme.com${currentPath}` });
    });
  }

  const breadcrumbsSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.item
    }))
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords || "business software recommendations, find business tools, software comparison, best business software, CRM software, project management tools, accounting software, marketing automation, software for startups, business tool finder, compare software, software guide, entrepreneur tools, small business software, SaaS comparison, business productivity tools, workflow automation, business management software"} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - btoolme`} />
      <meta property="og:site_name" content="btoolme" />
      <meta property="og:locale" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@btoolme" />
      <meta name="twitter:creator" content="@btoolme" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={`${title} - btoolme`} />

      {/* Additional SEO */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="btoolme" />

      {/* Schema.org Markup - Only include one schema of each type */}
      {schema ? (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ) : (
        <script type="application/ld+json">
          {JSON.stringify(defaultSchema)}
        </script>
      )}
      
      {/* FAQ Schema - Only render if faqItems are provided */}
      {faqItems && faqItems.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}

      {/* Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      {/* Breadcrumbs Schema */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbsSchema)}
      </script>
    </Helmet>
  );
}